"use client";

import { usePathname } from "next/navigation";
import { Footer } from "@/components/layout/Footer";
import { isContestProblemRoute, isJudgeWorkspaceRoute } from "@/lib/full-viewport-routes";
import type { Locale } from "@/lib/i18n";

/**
 * The site footer, on every Judge and contest page except the one that cannot
 * hold it.
 *
 * Until 2026-09-10 only `/judge` had a footer, because that page rendered one
 * itself; `/judge/problems`, `/judge/leaderboard`, `/contests`, a contest page
 * and a contest's rules all ended at the last card. `SiteLayout` skips the
 * marketing header and footer for this whole segment (`EX-PUB11`), so nothing
 * was left to supply one, and the shell is where it belongs rather than pasted
 * into each page.
 *
 * The exceptions are the two graded workspaces, and they are real ones: both
 * `ProblemWorkspaceClient` and (since `EX-PUB14`) `ContestProblemWorkspaceClient`
 * are `h-[calc(100dvh-68px)]`, the viewport minus this shell's own topbar, with
 * both split panes `overflow-hidden`. A footer under either would be unreachable
 * without giving a deliberately unscrollable shell a scrollbar. Every other page
 * in the segment, the contest page and its rules included, still gets one.
 *
 * Client-side because the decision is the pathname's, the same way `SiteLayout`
 * already decides. The values the footer needs are resolved on the server and
 * handed down, so this component fetches nothing.
 */
export function JudgeShellFooter({
  locale,
  siteName,
  tagline,
}: {
  locale: Locale;
  siteName?: string;
  tagline?: string;
}) {
  const pathname = usePathname();
  if (isJudgeWorkspaceRoute(pathname) || isContestProblemRoute(pathname)) return null;

  return <Footer compact locale={locale} siteName={siteName} tagline={tagline} />;
}
